import { useState } from 'react';
import styled from 'styled-components';

import { P } from '../../elements/Text';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import {
  useGetCompany,
  useGetCompanyOfficeHours,
} from '../../../hooks/use-hydra/admin';
import {
  CompanyResponse,
  LearningPlanItem,
  LEARNER_STATUS,
  OfficeHoursResponse,
} from '../../../api/admin';
import { useParams } from 'react-router-dom';
import { LEARNING_PLAN_UNIT_TYPE } from '../../LearningPlan/LearningPlanUnit';
import CohortProgress from './CohortProgress';
import {
  FiltersHorizontalWrapper,
  TextFilterPill,
  MultiSelectFilterPill,
  TextOrEmptyFilterPill,
} from '../../elements/Filter';
import CollapsibleSection, {
  CollapsibleSectionHeader,
  CollapsibleSectionLabel,
} from '../CollapsibleSection';

dayjs.extend(relativeTime);

export const calculateLearningPlanProgressFromPlan = (
  learningPlan: LearningPlanItem[]
): number | null => {
  let totalItems = 0;
  let completedItems = 0;

  for (const planItem of learningPlan) {
    if (planItem.type === LEARNING_PLAN_UNIT_TYPE.CONTENT_MODULE) {
      const coreActivitiesCount = planItem.coreLessonsCount || 0;
      const coreSubmittedCount = planItem.coreSubmittedLessonsCount || 0;

      totalItems += coreActivitiesCount;
      completedItems += Math.min(coreSubmittedCount, coreActivitiesCount);
    }
  }

  if (totalItems === 0) {
    return null;
  }

  return Math.round((completedItems / totalItems) * 100);
};
export const calculateLearningPlanProgress = (
  learnerId: number,
  companyData: CompanyResponse
): number | null => {
  const learner = companyData.learners?.find((l) => l.id === learnerId);
  if (!learner?.learningPlan) {
    return null;
  }

  let totalItems = 0;
  let completedItems = 0;

  for (const planItem of learner.learningPlan) {
    if (planItem.type === LEARNING_PLAN_UNIT_TYPE.CONTENT_MODULE) {
      const coreActivitiesCount = planItem.coreLessonsCount || 0;
      const coreSubmittedCount = planItem.coreSubmittedLessonsCount || 0;

      totalItems += coreActivitiesCount;
      completedItems += Math.min(coreSubmittedCount, coreActivitiesCount);
    } else if (planItem.type === LEARNING_PLAN_UNIT_TYPE.PROJECT) {
      totalItems += 1;
      if ((planItem.submittedExercisesCount || 0) > 0) {
        completedItems += 1;
      }
    }
  }

  if (totalItems === 0) {
    return null;
  }

  return Math.round((completedItems / totalItems) * 100);
};

export type SubTopicDetail = {
  name: string;
  type: LEARNING_PLAN_UNIT_TYPE;
  coreCompleted?: number;
  coreTotal?: number;
  stretchCompleted?: number;
  stretchTotal?: number;
  completed?: number;
  total?: number;
};

export type Topic = {
  name: string;
  iconUrl: string;
  exercises: number;
  details?: SubTopicDetail[];
};

export type LearnerProgress = {
  sessionId: string;
  learnerId: number;
  name: string;
  email: string;
  planProgress: number | null;
  topics: Topic[];
  status: LEARNER_STATUS;
  lastActiveDate: Date | null;
  invitedAt: Date | null; // Date when the learner was invited
  onboardedAt: Date | null; // Date when the learner completed onboarding
  mentor: CompanyResponse['cohorts'][number]['learners'][number]['mentor'];
  onboardingAnswers: CompanyResponse['cohorts'][number]['learners'][number]['onboardingAnswers'];
  progress: CompanyResponse['cohorts'][number]['learners'][number]['progress'];
  officeHours: null | {
    id: number;
    calendarEventId: string | null;
    memberId: number;
    notesSummary: string | null;
    notesLastUpdatedAt: Date | null;
    officeHours: {
      timestamp: Date;
      didAttend: boolean | null;
      notes: {
        goals: string;
        challenges: string;
        insights: string;
      } | null;
    }[];
  };
};

type Cohort = {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  learners: LearnerProgress[];
};

const mapCompanyDataToCohorts = ({
  companyData,
  officeHours,
}: {
  companyData: CompanyResponse | null | undefined;
  officeHours: OfficeHoursResponse | null | undefined;
}): Cohort[] => {
  if (!companyData?.cohorts) return [];

  return companyData.cohorts.map((cohort) => ({
    id: cohort.cycleId.toString(),
    name: `${dayjs.utc(cohort.startDate).format('MMM YYYY')} Cohort`,
    startDate: dayjs.utc(cohort.startDate).format('YYYY-MM-DD'),
    endDate: dayjs.utc(cohort.endDate).format('YYYY-MM-DD'),
    learners: cohort.learners.map((learner) => {
      const topics: Topic[] = learner.progress.topics.map((topic) => {
        let totalExercises = 0;
        const details: SubTopicDetail[] = [];

        topic.units.forEach((unit) => {
          if (unit.type === LEARNING_PLAN_UNIT_TYPE.CONTENT_MODULE) {
            const coreSubmitted = unit.coreSubmittedLessonsCount || 0;
            const stretchSubmitted = unit.stretchSubmittedLessonsCount || 0;
            totalExercises += coreSubmitted + stretchSubmitted;

            details.push({
              name: unit.content.title,
              type: LEARNING_PLAN_UNIT_TYPE.CONTENT_MODULE,
              coreCompleted: coreSubmitted,
              coreTotal: unit.coreLessonsCount || 0,
              stretchCompleted: stretchSubmitted,
              stretchTotal: unit.stretchLessonsCount || 0,
            });
          } else if (unit.type === LEARNING_PLAN_UNIT_TYPE.PROJECT) {
            const submittedExercises = unit.submittedExercisesCount || 0;
            totalExercises += submittedExercises;

            details.push({
              name: unit.title,
              type: LEARNING_PLAN_UNIT_TYPE.PROJECT,
              completed: submittedExercises,
              total: unit.exercisesCount || 0,
            });
          }
        });

        return {
          name: topic.name,
          exercises: totalExercises,
          iconUrl: topic.iconUrl,
          details: details.length > 0 ? details : undefined,
        };
      });

      const officeHourGroup = officeHours?.officeHoursGroups.find((group) =>
        group.members.some(
          (member) => member.id === learner.mentor?.officeHourGroupMemberId
        )
      );

      const groupMember = officeHourGroup?.members.find(
        (member) => member.id === learner.mentor?.officeHourGroupMemberId
      );

      return {
        sessionId: learner.sessionId.toString(),
        learnerId: learner.learnerId,
        name: learner.name,
        email: learner.email,
        planProgress: calculateLearningPlanProgress(
          learner.learnerId,
          companyData
        ),
        topics,
        status: learner.status,
        lastActiveDate: learner.lastActiveAt,
        invitedAt: learner.invitedAt,
        onboardedAt: learner.onboardedAt,
        mentor: learner.mentor,
        onboardingAnswers: learner.onboardingAnswers,
        progress: learner.progress,
        officeHours:
          officeHourGroup && groupMember
            ? {
                id: officeHourGroup.id,
                calendarEventId: officeHourGroup?.calendarEventId,
                memberId: groupMember.id,
                notesSummary: groupMember?.noteSummary,
                notesLastUpdatedAt: groupMember?.notesLastUpdatedAt,
                officeHours: officeHourGroup?.officeHours.map((officeHour) => ({
                  timestamp: officeHour.timestamp,
                  didAttend:
                    officeHour.members.find(
                      (member) =>
                        member.memberId ===
                        learner.mentor?.officeHourGroupMemberId
                    )?.didAttend ?? null,
                  notes:
                    officeHour.members.find(
                      (member) =>
                        member.memberId ===
                        learner.mentor?.officeHourGroupMemberId
                    )?.notes ?? null,
                })),
              }
            : null,
      };
    }),
  }));
};

const ProgressWrapper = styled.div`
  padding: 24px 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
  color: ${({ theme }) => theme.colors.textPrimary};
  width: 100%;
  /* min-width: min-content; */
`;

const getCohortStatusLabel = (startDate: string, endDate: string): string => {
  const now = dayjs();
  const start = dayjs(startDate);
  const end = dayjs(endDate);

  if (now.isAfter(start) && now.isBefore(end)) {
    return 'Ongoing';
  } else if (now.isBefore(start)) {
    return start.fromNow();
  } else {
    return end.fromNow();
  }
};

export default function Progress() {
  const { id } = useParams<{ id: string }>();

  const { data: companyData } = useGetCompany(parseInt(id));
  const { data: officeHours } = useGetCompanyOfficeHours(parseInt(id));

  const cohorts = mapCompanyDataToCohorts({
    companyData,
    officeHours,
  });

  const [selectedStatuses, setSelectedStatuses] = useState<LEARNER_STATUS[]>(
    []
  );

  const [expandedCohorts, setExpandedCohorts] = useState<
    Record<string, boolean>
  >(() => {
    const initialState: Record<string, boolean> = {};
    cohorts.forEach((cohort, index) => {
      initialState[cohort.id] = index === 0;
    });
    return initialState;
  });

  const toggleCohort = (cohortId: string) => {
    setExpandedCohorts((prev) => ({ ...prev, [cohortId]: !prev[cohortId] }));
  };

  const STATUS_OPTIONS: { label: string; value: LEARNER_STATUS }[] = [
    { label: 'Invited', value: LEARNER_STATUS.INVITED },
    { label: 'Onboarded', value: LEARNER_STATUS.ONBOARDED },
    { label: 'Active', value: LEARNER_STATUS.ACTIVE },
    { label: 'Inactive', value: LEARNER_STATUS.INACTIVE },
    { label: 'Invite Expired', value: LEARNER_STATUS.INVITE_EXPIRED },
    { label: 'Cohort Ended', value: LEARNER_STATUS.COHORT_ENDED },
  ];

  const [learnerName, setLearnerName] = useState('');

  const [mentorName, setMentorName] = useState<string | null>('');

  const filterLearners = (learners: LearnerProgress[]) => {
    let filtered = learners;
    if (selectedStatuses.length > 0) {
      filtered = filtered.filter((learner) =>
        selectedStatuses.includes(learner.status)
      );
    }
    if (learnerName.trim()) {
      const search = learnerName.trim().toLowerCase();
      filtered = filtered.filter((learner) =>
        learner.name.toLowerCase().includes(search)
      );
    }
    if (mentorName === null || mentorName?.trim()) {
      const search = mentorName?.trim().toLowerCase() ?? '';
      filtered = filtered.filter((learner) => {
        const hasMentor = learner.mentor && learner.mentor.name;
        const matchesMentor =
          mentorName?.trim() &&
          hasMentor &&
          learner.mentor?.name?.toLowerCase().includes(search);
        const isNoMentor =
          mentorName === null && (!learner.mentor || !learner.mentor.name);
        return matchesMentor || isNoMentor;
      });
    }
    return filtered;
  };

  return (
    <ProgressWrapper>
      <FiltersHorizontalWrapper>
        <TextFilterPill
          label="Learner"
          value={learnerName}
          placeholder="Type the learner name..."
          onChange={setLearnerName}
        />
        <TextOrEmptyFilterPill
          label="Mentor"
          nullLabel="No mentor assigned"
          value={mentorName}
          placeholder="Type the mentor name..."
          onChange={(value) => setMentorName(value)}
        />
        <MultiSelectFilterPill
          label="Status"
          values={selectedStatuses}
          options={STATUS_OPTIONS}
          onChange={(values) => setSelectedStatuses(values as LEARNER_STATUS[])}
        />
      </FiltersHorizontalWrapper>
      {cohorts.length === 0 && (
        <P style={{ fontStyle: 'italic' }}>No cohorts created yet.</P>
      )}
      {cohorts.map((cohort) => {
        const isExpanded = expandedCohorts[cohort.id] ?? false;
        const filteredLearners = filterLearners(cohort.learners);
        return (
          <CollapsibleSection
            key={cohort.id}
            headerLeft={
              <CollapsibleSectionHeader>
                {dayjs.utc(cohort.startDate).format('MMM D, YYYY')} -{' '}
                {dayjs.utc(cohort.endDate).format('MMM D, YYYY')}
              </CollapsibleSectionHeader>
            }
            headerRight={
              <CollapsibleSectionLabel>
                {getCohortStatusLabel(cohort.startDate, cohort.endDate)}
              </CollapsibleSectionLabel>
            }
            expanded={isExpanded}
            onToggle={() => toggleCohort(cohort.id)}
          >
            <CohortProgress
              learners={filteredLearners}
              cohortId={cohort.id}
              learnerRoles={Object.fromEntries(
                (companyData?.learners ?? []).map((learner) => [
                  learner.id.toString(),
                  learner.onboardingAnswers.role,
                ])
              )}
            />
          </CollapsibleSection>
        );
      })}
    </ProgressWrapper>
  );
}
